import React, { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { usePageImages, FIELD_LABELS, FIELD_COLORS } from "./ModuloShared";

const uid = () => Math.random().toString(36).slice(2, 10);
const clamp = (v) => Math.max(0, Math.min(1, v));
const SIZES = {
  text: [["Corto", 0.15, 0.022], ["Medio", 0.3, 0.022], ["Lungo", 0.55, 0.022]],
  date: [["Corto", 0.1, 0.022], ["Medio", 0.14, 0.022], ["Lungo", 0.2, 0.022]],
  checkbox: [["Piccola", 0.02, 0.014], ["Media", 0.025, 0.018], ["Grande", 0.035, 0.025]],
  signature_tecnico: [["Piccola", 0.2, 0.05], ["Media", 0.3, 0.07], ["Grande", 0.4, 0.1]],
  signature_cliente: [["Piccola", 0.2, 0.05], ["Media", 0.3, 0.07], ["Grande", 0.4, 0.1]],
};
const fit = (f, patch) => { const n = { ...f, ...patch }; n.w = Math.min(n.w, 1); n.h = Math.min(n.h, 1); n.x = Math.min(clamp(n.x), 1 - n.w); n.y = Math.min(clamp(n.y), 1 - n.h); return n; };

const FieldBox = ({ f, selected, canvas, onSelect, onUpdate }) => {
  const st = useRef(null);
  const start = (mode) => (e) => {
    e.stopPropagation(); e.preventDefault(); onSelect(f.id);
    e.currentTarget.setPointerCapture(e.pointerId);
    st.current = { mode, cx: e.clientX, cy: e.clientY, r: canvas.current.getBoundingClientRect(), f };
  };
  const move = (e) => {
    const s = st.current; if (!s) return;
    e.stopPropagation();
    const dx = (e.clientX - s.cx) / s.r.width, dy = (e.clientY - s.cy) / s.r.height;
    if (s.mode === "move") onUpdate(f.id, fit(s.f, { x: s.f.x + dx, y: s.f.y + dy }));
    else onUpdate(f.id, fit(s.f, { w: Math.max(0.01, Math.min(1 - s.f.x, s.f.w + dx)), h: Math.max(0.008, Math.min(1 - s.f.y, s.f.h + dy)) }));
  };
  const end = (e) => { e.stopPropagation(); st.current = null; };
  const c = FIELD_COLORS[f.type];
  return (
    <div onPointerDown={start("move")} onPointerMove={move} onPointerUp={end} onPointerCancel={end} title={f.label || FIELD_LABELS[f.type]}
      className="absolute text-[9px] font-bold text-left px-0.5 cursor-move" data-testid={`tpl-field-${f.id}`}
      style={{ touchAction: "none", left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`, border: `2px solid ${c}`, background: selected ? `${c}40` : `${c}18`, color: c, zIndex: selected ? 2 : 1 }}>
      <span className="block overflow-hidden whitespace-nowrap pointer-events-none">{f.label || FIELD_LABELS[f.type]}</span>
      {selected && <span onPointerDown={start("resize")} onPointerMove={move} onPointerUp={end} onPointerCancel={end} className="absolute -right-2 -bottom-2 w-4 h-4 rounded-full border-2 border-white cursor-nwse-resize shadow"
        style={{ background: c, touchAction: "none" }} data-testid={`tpl-field-resize-${f.id}`} />}
    </div>
  );
};

const PageCanvas = ({ url, pageIdx, fields, tool, selected, onAdd, onSelect, onUpdate }) => {
  const ref = useRef(null);
  const [drag, setDrag] = useState(null);
  const rel = (e) => { const r = ref.current.getBoundingClientRect(); return [clamp((e.clientX - r.left) / r.width), clamp((e.clientY - r.top) / r.height)]; };
  const down = (e) => { if (e.target !== ref.current && e.target.tagName !== "IMG") return; ref.current.setPointerCapture(e.pointerId); const [x, y] = rel(e); setDrag({ x0: x, y0: y, x1: x, y1: y }); };
  const move = (e) => { if (drag) { const [x, y] = rel(e); setDrag({ ...drag, x1: x, y1: y }); } };
  const up = () => {
    if (!drag) return;
    const x = Math.min(drag.x0, drag.x1), y = Math.min(drag.y0, drag.y1);
    let w = Math.abs(drag.x1 - drag.x0), h = Math.abs(drag.y1 - drag.y0);
    setDrag(null);
    if (w < 0.01 && h < 0.006) { [, w, h] = SIZES[tool][1]; }
    onAdd(fit({ id: uid(), page: pageIdx, type: tool, label: "" }, { x, y, w, h }));
  };
  const box = drag && { left: `${Math.min(drag.x0, drag.x1) * 100}%`, top: `${Math.min(drag.y0, drag.y1) * 100}%`, width: `${Math.abs(drag.x1 - drag.x0) * 100}%`, height: `${Math.abs(drag.y1 - drag.y0) * 100}%` };
  return (
    <div ref={ref} className="relative select-none cursor-crosshair border shadow-sm" style={{ touchAction: "pan-y pinch-zoom" }} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => setDrag(null)} data-testid={`tpl-page-${pageIdx}`}>
      {url ? <img src={url} alt={`Pagina ${pageIdx + 1}`} className="w-full block pointer-events-none" draggable={false} /> : <div className="h-96 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {fields.map((f) => <FieldBox key={f.id} f={f} selected={selected === f.id} canvas={ref} onSelect={onSelect} onUpdate={onUpdate} />)}
      {box && <div className="absolute border-2 border-dashed pointer-events-none" style={{ ...box, borderColor: FIELD_COLORS[tool] }} />}
    </div>
  );
};

export const ModuloTemplateEditor = ({ commessa, onClose }) => {
  const [tpl, setTpl] = useState(undefined);
  const [fields, setFields] = useState([]);
  const [tool, setTool] = useState("text");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const urls = usePageImages(tpl);

  useEffect(() => {
    api.get(`/commesse/${commessa.id}/modulo-template`).then(({ data }) => { setTpl(data); setFields(data?.fields || []); }).catch(() => setTpl(null));
  }, [commessa.id]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post(`/commesse/${commessa.id}/modulo-template`, fd); setTpl(data); setFields(data.fields || []); toast.success("PDF caricato: ora disegna i campi sulle pagine"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.put(`/commesse/${commessa.id}/modulo-template/fields`, { fields }); toast.success(`Modulo salvato (${fields.length} campi)`); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const sel = fields.find((f) => f.id === selected);
  const updId = (id, patch) => setFields((fs) => fs.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const upd = (patch) => updId(selected, patch);
  useEffect(() => {
    const k = (e) => {
      if (!selected || ["INPUT", "SELECT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
      if (!d) return;
      e.preventDefault();
      const step = e.shiftKey ? 0.01 : 0.002;
      setFields((fs) => fs.map((f) => (f.id === selected ? fit(f, { x: f.x + d[0] * step, y: f.y + d[1] * step }) : f)));
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [selected]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" data-testid="modulo-template-editor">
      <div className="p-3 border-b flex flex-wrap items-center gap-2" style={{ borderColor: "var(--border)" }}>
        <button onClick={onClose} className="p-2 rounded hover:bg-black/5" data-testid="tpl-close"><X className="w-5 h-5" /></button>
        <div className="font-bold flex-1 min-w-[200px]">Modulo sopralluogo · {commessa.name}{tpl?.name ? <span className="font-normal text-sm" style={{ color: "var(--text-muted)" }}> · {tpl.name}</span> : null}</div>
        <label className="btn-secondary cursor-pointer" data-testid="tpl-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {tpl ? "Sostituisci PDF" : "Carica PDF"}
          <input type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid="tpl-upload-input" />
        </label>
        {tpl && <button onClick={save} disabled={busy} className="btn-primary md:mr-[390px]" data-testid="tpl-save"><Save className="w-4 h-4" /> Salva campi</button>}
      </div>
      {tpl === undefined ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : !tpl ? (
        <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Carica il PDF del modulo della commessa (con logo): poi indicherai i campi da compilare.</div>
      ) : (
        <div className="flex-1 flex flex-col md:flex-row min-h-0">
          <div className="md:w-72 p-3 border-b md:border-b-0 md:border-r space-y-3 overflow-auto" style={{ borderColor: "var(--border)" }}>
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tipo di campo da disegnare</div>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-1.5">
              {Object.entries(FIELD_LABELS).map(([k, l]) => (
                <button key={k} onClick={() => setTool(k)} className="text-left text-sm px-2 py-1.5 rounded border" data-testid={`tpl-tool-${k}`}
                  style={{ borderColor: tool === k ? FIELD_COLORS[k] : "var(--border)", background: tool === k ? `${FIELD_COLORS[k]}15` : "#fff", color: FIELD_COLORS[k], fontWeight: tool === k ? 700 : 500 }}>{l}</button>
              ))}
            </div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>Trascina sulla pagina per disegnare il riquadro (o tocca per un riquadro standard). Tocca un campo per selezionarlo, spostarlo o ridimensionarlo.</div>
            {sel && (
              <div className="p-2 rounded border space-y-2" style={{ borderColor: FIELD_COLORS[sel.type] }} data-testid="tpl-selected">
                <input value={sel.label} onChange={(e) => upd({ label: e.target.value })} placeholder="Nome campo (es. Potenza contatore)" className="w-full text-sm px-2 py-1 border rounded" data-testid="tpl-field-label" />
                <select value={sel.type} onChange={(e) => { const t = e.target.value; upd(fit(sel, { type: t, w: SIZES[t][1][1], h: SIZES[t][1][2] })); }} className="w-full text-sm px-2 py-1 border rounded" data-testid="tpl-field-type">
                  {Object.entries(FIELD_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <div className="text-[11px] font-semibold" style={{ color: "var(--text-muted)" }}>Lunghezza</div>
                <div className="grid grid-cols-3 gap-1">
                  {SIZES[sel.type].map(([l, w, h]) => {
                    const on = Math.abs(sel.w - w) < 0.001 && Math.abs(sel.h - h) < 0.001;
                    return <button key={l} onClick={() => upd(fit(sel, { w, h }))} className="text-xs px-1 py-1 rounded border" data-testid={`tpl-size-${l.toLowerCase()}`}
                      style={{ borderColor: on ? FIELD_COLORS[sel.type] : "var(--border)", fontWeight: on ? 700 : 500, color: on ? FIELD_COLORS[sel.type] : undefined }}>{l}</button>;
                  })}
                </div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Trascina il campo per spostarlo, il pallino nell'angolo per allungarlo/accorciarlo. Frecce tastiera per spostamenti fini.</div>
                <button onClick={() => { setFields((fs) => fs.filter((f) => f.id !== selected)); setSelected(null); }} className="btn-secondary text-xs w-full justify-center" style={{ color: "#B30000" }} data-testid="tpl-field-delete"><Trash2 className="w-3.5 h-3.5" /> Elimina campo</button>
              </div>
            )}
            <div className="text-xs" data-testid="tpl-field-count">{fields.length} campi</div>
          </div>
          <div className="flex-1 overflow-auto p-3 md:p-6 space-y-6" style={{ background: "#F1F5F9" }}>
            {tpl.pages.map((_, i) => (
              <div key={i} className="max-w-3xl mx-auto">
                <div className="text-xs mb-1 font-semibold" style={{ color: "var(--text-muted)" }}>Pagina {i + 1}</div>
                <PageCanvas url={urls[i]} pageIdx={i} fields={fields.filter((f) => f.page === i)} tool={tool} selected={selected}
                  onAdd={(f) => { setFields((fs) => [...fs, f]); setSelected(f.id); }} onSelect={setSelected} onUpdate={updId} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
